import { IProduct } from '@/entities/product/model/types';
import { ICartItem } from '../_store/types';
import { IProductVariant, ICategory } from '../_types';

export interface PurchaseItem {
  product_id?: string | null;
  product_name?: string | null;
  price?: number | string | null;
  price_with_discount?: number | string | null;
  quantity?: number | string | null;
  order_modifiers?:
    | readonly {
        modifier_price?: number | string | null;
        modifier_quantity?: number | string | null;
      }[]
    | null;
}

export interface PurchaseOrder {
  order_id?: string | null;
  external_order_id?: string | null;
  total_price?: number | string | null;
  delivery_price?: number | string | null;
  promocode?: string | null;
  products?: readonly PurchaseItem[] | null;
}

export interface PurchaseTrackingItem {
  item_name: string;
  item_id: string;
  price: number;
  quantity: number;
}

interface PurchaseEventPayload {
  eventId: string;
  pixel: {
    value: number;
    currency: string;
    content_ids: string[];
    content_type: 'product';
    num_items: number;
    contents: {
      id: string;
      quantity: number;
      item_price: number;
    }[];
  };
  ecommerce: {
    currency: string;
    affiliation: string;
    value: number;
    coupon: string;
    tax: string;
    transaction_id: string;
    shipping: number;
    items: {
      item_name: string;
      item_id: string;
      price: number;
      quantity: number;
    }[];
  };
}

const PIXEL_PURCHASE_DEDUP_PREFIX = 'meta_pixel_purchase_';

function toFiniteNumber(value: unknown): number | null {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeCurrency(currency?: string | null): string {
  return currency?.trim().toUpperCase() || 'UZS';
}

export function buildPurchaseItems(
  products?: readonly PurchaseItem[] | null,
): PurchaseTrackingItem[] {
  return (products ?? []).flatMap((item) => {
    const id = item.product_id?.trim();
    const basePrice = toFiniteNumber(item.price_with_discount ?? item.price);
    const quantity = toFiniteNumber(item.quantity);
    const modifierPrice = (item.order_modifiers ?? []).reduce(
      (sum, modifier) => {
        const price = toFiniteNumber(modifier.modifier_price);
        const modifierQuantity = toFiniteNumber(modifier.modifier_quantity);

        return price === null || modifierQuantity === null
          ? sum
          : sum + price * modifierQuantity;
      },
      0,
    );
    const price = basePrice === null ? null : basePrice + modifierPrice;

    if (!id || price === null || price < 0 || quantity === null || quantity <= 0) {
      return [];
    }

    return [
      {
        item_name: item.product_name?.trim() || id,
        item_id: id,
        price,
        quantity,
      },
    ];
  });
}

export function buildPurchaseEventPayload({
  order,
  currency,
}: {
  order?: PurchaseOrder;
  currency?: string | null;
}): PurchaseEventPayload | null {
  const value = toFiniteNumber(order?.total_price);
  const transactionId = order?.order_id || order?.external_order_id;

  if (value === null || !transactionId) return null;

  const items = buildPurchaseItems(order?.products);

  if (!items.length) return null;

  const shipping = toFiniteNumber(order?.delivery_price) ?? 0;
  const normalizedCurrency = normalizeCurrency(currency);

  return {
    eventId: transactionId,
    pixel: {
      value,
      currency: normalizedCurrency,
      content_ids: items.map((item) => item.item_id),
      content_type: 'product',
      num_items: items.reduce((sum, item) => sum + item.quantity, 0),
      contents: items.map((item) => ({
        id: item.item_id,
        quantity: item.quantity,
        item_price: item.price,
      })),
    },
    ecommerce: {
      currency: normalizedCurrency,
      affiliation: 'Online Store',
      value,
      coupon: order?.promocode || '',
      tax: '0.00',
      transaction_id: transactionId,
      shipping,
      items,
    },
  };
}

export default function useGTM() {
  const safePush = (data: any) => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  const safeFbq = (
    a: string = 'track',
    event: string,
    parameters?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => {
    if (typeof window === 'undefined' || typeof window.fbq === 'undefined')
      return;
    window.fbq(a, event, parameters, options);
  };

  const addItem = ({
    data,
    quantity,
  }: {
    data?: Partial<IProduct | IProductVariant>;
    quantity?: number;
  }) => {
    safePush({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            item_name: data?.title?.ru,
            item_id: data?.id,
            price: data?.out_price,
            item_category: data?.categories?.[0]?.title?.ru,
            index: data?.order,
            quantity,
          },
        ],
      },
    });
  };

  const selectItem = ({
    data,
    name,
  }: {
    data?: Partial<IProduct | IProductVariant>;
    name?: string;
  }) => {
    safePush({
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_name: data?.title?.ru,
            item_id: data?.id,
            item_brand: name,
            item_category: data?.slug,
            index: data?.order,
            price: data?.out_price,
            quantity: 1,
          },
        ],
      },
    });
  };

  const addToCartAds = ({
    data,
  }: {
    data?: Partial<IProduct | IProductVariant>;
  }) => {
    safePush({
      event: 'add_to_cart_ads',
      value: data?.out_price || 0,
      items: [{ id: data?.id, google_business_vertical: 'retail' }],
    });
  };

  const addToCartFb = ({
    data,
    currency,
  }: {
    data?: Partial<IProduct | IProductVariant>;
    currency?: string;
  }) => {
    const payload = {
      value: data?.out_price || 0,
      content_ids: [data?.id],
      content_type: 'product',
      currency: currency || 'UZS',
    };
    safePush({
      event: 'add_to_cart_fb',
      ...payload,
    });
    safeFbq('track', 'AddToCart', payload);
  };

  const removeItem = ({
    cart,
    name,
  }: {
    cart?: ICartItem[];
    name?: string;
  }) => {
    safePush({
      event: 'remove_from_cart',
      ecommerce: {
        items:
          cart?.map((el, idx) => ({
            item_brand: name,
            item_name: el.product_name,
            item_category: el.category_name,
            item_id: el.product_id,
            quantity: el.quantity,
            price: el.price,
            index: idx,
          })) || [],
      },
    });
  };

  const beginCheckout = ({
    cart,
    currency,
  }: {
    cart?: ICartItem[];
    currency?: string;
  }) => {
    safePush({
      event: 'begin_checkout',
      ecommerce: {
        items: cart?.map((item, idx) => ({
          item_name: item?.product_name,
          item_id: item?.product_id,
          price: item?.price_with_discount,
          item_category: item?.category_name,
          item_list_name: 'Cart',
          item_list_id: 'CH123',
          index: idx,
          quantity: item?.quantity,
        })),
      },
    });

    const num_items =
      cart?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
    const value =
      cart?.reduce(
        (sum, item) =>
          sum + (item.price_with_discount || 0) * (item.quantity || 0),
        0,
      ) || 0;

    safeFbq('track', 'InitiateCheckout', {
      num_items,
      value,
      currency: currency || 'UZS',
    });
  };

  const purchase = ({
    data,
    currency,
  }: {
    data?: PurchaseOrder;
    currency?: string;
  }) => {
    const payload = buildPurchaseEventPayload({ order: data, currency });
    if (!payload) return;

    const dedupKey = `${PIXEL_PURCHASE_DEDUP_PREFIX}${payload.eventId}`;
    if (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(dedupKey)
    ) {
      return;
    }

    safePush({
      event: 'purchase',
      ecommerce: payload.ecommerce,
    });

    safeFbq('track', 'Purchase', payload.pixel, { eventID: payload.eventId });

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(dedupKey, '1');
    }
  };

  const viewItem = ({
    data,
    currency,
    quantity = 1,
  }: {
    data?: Partial<IProduct | IProductVariant>;
    currency?: string;
    quantity?: number;
  }) => {
    if (!data?.id) return;

    safePush({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            item_name: data?.title?.ru,
            item_id: data?.id,
            price: data?.out_price,
            item_category: data?.categories?.[0]?.title?.ru,
            index: data?.order,
            quantity,
          },
        ],
      },
    });
    safePush({
      event: 'view_item_ads',
      value: data?.out_price,
      items: [
        {
          id: data?.id,
          google_business_vertical: 'retail',
        },
      ],
    });
    const fbPayload = {
      value: data?.out_price || 0,
      content_ids: [data?.id],
      content_type: 'product',
      currency: currency || 'UZS',
    };
    safePush({
      event: 'view_item_fb',
      ...fbPayload,
    });
    safeFbq('track', 'ViewContent', fbPayload);
  };

  const viewItemList = ({
    products,
    category,
    shipperName,
    currency = 'UZS',
  }: {
    products: IProduct[];
    category?: ICategory;
    shipperName?: string;
    currency?: string;
  }) => {
    if (!products?.length) return;
    safePush({ ecommerce: null });

    safePush({
      event: 'view_item_list',
      ecommerce: {
        items: products.map((item) => ({
          item_name: item?.title?.ru,
          item_id: item?.id,
          price: item?.out_price,
          item_brand: shipperName,
          item_category: category?.title?.ru,
          index: item?.order,
          quantity: 1,
        })),
      },
    });

    const allPrice = products.reduce(
      (sum, el) => sum + (el?.out_price || 0),
      0,
    );

    viewItemListAds(products, allPrice);
    viewItemListFb(products, allPrice, currency);
  };

  const viewItemListAds = (
    products: { id: string; out_price?: number }[],
    total: number,
  ) => {
    safePush({
      event: 'view_item_list_ads',
      value: total,
      items: products.map((el) => ({
        id: el.id,
        google_business_vertical: 'retail',
      })),
    });
  };

  const viewItemListFb = (
    products: { id: string; out_price?: number }[],
    total: number,
    currency: string,
  ) => {
    safePush({
      event: 'view_item_list_fb',
      value: total,
      content_ids: products.map((el) => el.id),
      content_type: 'product',
      currency,
    });
  };

  const viewSearchResultsAds = (
    products: { id: string; out_price?: number }[],
    total: number,
  ) => {
    safePush({
      event: 'view_search_results_ads',
      value: total,
      items: products.map((el) => ({
        id: el.id,
        google_business_vertical: 'retail',
      })),
    });
  };

  const viewSearchResultsFb = (
    products: { id: string; out_price?: number }[],
    total: number,
    currency: string,
  ) => {
    const payload = {
      value: total,
      content_ids: products.map((el) => el.id),
      content_type: 'product',
      currency,
    };
    safePush({
      event: 'view_item_results_fb',
      ...payload,
    });
    safeFbq('trackCustom', 'ViewSearchResults', payload);
  };

  const sendGTM = (
    key: string,
    {
      product,
      quantity,
      cart,
      order,
      currency,
      name,
      products,
      category,
      shipperName,
    }: {
      product?: Partial<IProduct | IProductVariant>;
      quantity?: number;
      cart?: ICartItem[];
      order?: PurchaseOrder;
      currency?: string;
      name?: string;
      products?: IProduct[] | { id: string; out_price?: number }[];
      category?: ICategory;
      shipperName?: string;
    },
  ) => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    safePush({ ecommerce: null });

    switch (key) {
      case 'add_to_cart':
        addItem({ data: product, quantity });
        break;
      case 'select_item':
        selectItem({ data: product, name });
        break;
      case 'remove_from_cart':
        removeItem({ cart });
        break;
      case 'begin_checkout':
        beginCheckout({ cart, currency });
        break;
      case 'purchase':
        purchase({ data: order, currency });
        break;
      case 'add_to_cart_ads':
        addToCartAds({ data: product });
        break;
      case 'add_to_cart_fb':
        addToCartFb({ data: product, currency });
        break;
      case 'view_item':
        viewItem({ data: product, currency, quantity });
        break;
      case 'view_item_list':
        viewItemList({
          products: (products || []) as IProduct[],
          category,
          shipperName,
          currency,
        });
        break;
      case 'view_item_list_ads':
        viewItemListAds(
          products || [],
          (products || []).reduce((s, p) => s + (p?.out_price || 0), 0),
        );
        break;
      case 'view_item_list_fb':
        viewItemListFb(
          products || [],
          (products || []).reduce((s, p) => s + (p?.out_price || 0), 0),
          currency || 'UZS',
        );
        break;
      case 'view_search_results_ads':
        viewSearchResultsAds(
          products || [],
          (products || []).reduce((s, p) => s + (p?.out_price || 0), 0),
        );
        break;
      case 'view_item_results_fb':
        viewSearchResultsFb(
          products || [],
          (products || []).reduce((s, p) => s + (p?.out_price || 0), 0),
          currency || 'UZS',
        );
        break;
      default:
        safePush({ event: key });
        break;
    }
  };

  return { sendGTM };
}
